import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { apiCall } from '../utils/api';
import { toast } from '../components/ui/sonner';
import { validateTimeRange } from '../utils/validation';
import NotificationBell from '../components/NotificationBell';
import EnhancedJobPosting from '../components/employer/EnhancedJobPosting';
import PayRateGate from '../components/shared/PayRateGate';
import { 
  Building2, MapPin, Calendar, Clock, Users, CheckCircle, AlertTriangle, AlertCircle,
  ClipboardList, RefreshCw, ChevronRight, Eye, Plus, FileText, Search,
  Home, Briefcase, Activity, Target, Award, MessageSquare, X, Check,
  LogOut, Settings, Bell, Menu, BarChart3, TrendingUp, Sparkles, Trash2
} from 'lucide-react';

// iter91 refactor — extracted from repeated chained ternaries.
// Centralising the status → tailwind-class mapping means adding a new state
// (e.g. `paused`) is a one-liner instead of a scavenger-hunt through five
// component branches.
const SHIFT_STATUS_CLASSES = {
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  assigned: 'bg-purple-100 text-purple-700',
};
const shiftStatusClass = (status) => SHIFT_STATUS_CLASSES[status] || 'bg-slate-100 text-slate-600';

const TASK_STATUS_CLASSES = {
  needs_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
};
const taskStatusClass = (status) => TASK_STATUS_CLASSES[status] || 'bg-slate-100 text-slate-600';

const TASK_ROW_CLASSES = {
  needs_review: 'border-amber-300 bg-amber-50',
  approved: 'border-green-300 bg-green-50',
  rejected: 'border-red-300 bg-red-50',
};
const taskRowClass = (status) => TASK_ROW_CLASSES[status] || 'border-slate-200';

const TASK_PRIORITY_CLASSES = {
  high: 'bg-red-100 text-red-700',
  urgent: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
};
const taskPriorityClass = (priority) => TASK_PRIORITY_CLASSES[priority] || 'bg-slate-100 text-slate-600';

const LEADERBOARD_MEDALS = ['bg-yellow-500', 'bg-gray-400', 'bg-amber-600'];
const medalClass = (rank) => LEADERBOARD_MEDALS[rank] || 'bg-slate-400';

export default function PMDashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isConnected } = useWebSocket();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showPostShiftModal, setShowPostShiftModal] = useState(false);
  const [showEnhancedJobPosting, setShowEnhancedJobPosting] = useState(false);
  const [taskTemplates, setTaskTemplates] = useState({ builtin: [], custom: [] });
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Allow access if user is employer (any role) or specifically a program_manager/location_manager
    if (!user || user.role !== 'employer') {
      toast.error('Access denied. Employer credentials required.');
      navigate('/pm-login');
      return;
    }
    loadDashboard();
    loadTaskTemplates();
  }, [user, navigate]);

  useEffect(() => {
    if (activeView === 'shifts' && dashboard) {
      loadShifts();
    }
  }, [activeView, selectedLocation, dashboard]);

  // Live sync: PM board reflects shift/program/location changes instantly.
  useLiveRefresh(['shifts', 'jobs', 'programs', 'locations', 'group_homes', 'attendance', 'tasks'], () => {
    loadDashboard();
    if (activeView === 'shifts') loadShifts();
  });

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/employer/program-manager/dashboard');
      setDashboard(response);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadShifts = async () => {
    try {
      let url = '/api/employer/group-homes/shifts';
      if (selectedLocation !== 'all') {
        url += `?location_id=${selectedLocation}`;
      }
      const response = await apiCall(url);
      setShifts(response.shifts || []);
    } catch (error) {
      console.error('Failed to load shifts:', error);
    }
  };

  const loadShiftTasks = async (jobId) => {
    try {
      const response = await apiCall(`/api/employer/shifts/${jobId}/tasks`);
      setTasks(response.tasks || []);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  };

  const loadTaskTemplates = async () => {
    try {
      const response = await apiCall('/api/employer/tasks/templates');
      setTaskTemplates({
        builtin: response.builtin_templates || [],
        custom: response.custom_templates || []
      });
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const reviewTask = async (taskId, status, feedback) => {
    try {
      await apiCall(`/api/employer/tasks/${taskId}/review`, {
        method: 'POST',
        body: JSON.stringify({ status, feedback })
      });
      toast.success(`Task ${status}`);
      loadDashboard();
      if (selectedShift) {
        loadShiftTasks(selectedShift._id);
      }
    } catch (error) {
      toast.error('Failed to review task');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/pm-login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 text-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const role = dashboard?.role || 'program_manager';
  const scope = dashboard?.scope || {};
  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      {/* Top Navigation */}
      <nav className="bg-gradient-to-r from-purple-700 via-purple-600 to-pink-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Program Name */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <Home className="w-6 h-6" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="font-bold text-lg">Program Manager Dashboard</h1>
                  <p className="text-xs text-purple-200">
                    {scope.program?.name || scope.location?.name || 'All Programs'}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <button
                onClick={() => setActiveView('overview')}
                className={`px-3 py-2 rounded-lg font-medium transition ${
                  activeView === 'overview' ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveView('shifts')}
                className={`px-3 py-2 rounded-lg font-medium transition ${
                  activeView === 'shifts' ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
              >
                Shifts
              </button>
              <button
                onClick={() => setActiveView('tasks')}
                className={`px-3 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                  activeView === 'tasks' ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
              >
                Tasks
                {stats.tasks_needing_review > 0 && (
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                    {stats.tasks_needing_review}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveView('analytics')}
                className={`px-3 py-2 rounded-lg font-medium transition ${
                  activeView === 'analytics' ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
              >
                Analytics
              </button>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              {/* Connection Status */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full">
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-amber-400'}`}></div>
                <span className="text-xs">{isConnected ? 'Live' : 'Reconnecting'}</span>
              </div>

              {/* Notifications */}
              <NotificationBell />

              {/* User Menu */}
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-purple-200 capitalize">{role.replace('_', ' ')}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition text-sm"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 hover:bg-white/10 rounded-lg"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/20 px-4 py-3 space-y-2">
            {['overview', 'shifts', 'tasks', 'analytics'].map(tab => (
              <button
                key={tab}
                onClick={() => { setActiveView(tab); setMobileMenuOpen(false); }}
                className={`w-full text-left px-4 py-2 rounded-lg capitalize ${
                  activeView === tab ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
              >
                {tab}
                {tab === 'tasks' && stats.tasks_needing_review > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-500 text-xs rounded-full">
                    {stats.tasks_needing_review}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Location Filter (for PMs with multiple locations) */}
        {role === 'program_manager' && scope.locations?.length > 1 && (
          <div className="mb-6 flex items-center gap-4 bg-white p-4 rounded-xl border border-slate-200">
            <MapPin className="w-5 h-5 text-purple-600" />
            <label className="text-sm font-medium text-slate-700">Filter by Location:</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-800"
            >
              <option value="all">All Locations ({scope.locations.length})</option>
              {scope.locations.map(loc => (
                <option key={loc._id} value={loc._id}>{loc.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Overview Tab */}
        {activeView === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatsCard
                icon={<Calendar className="w-8 h-8" />}
                value={stats.today_shifts || 0}
                label="Today's Shifts"
                color="blue"
                badge="Today"
              />
              <StatsCard
                icon={<Activity className="w-8 h-8" />}
                value={stats.active_shifts || 0}
                label="Active Shifts"
                color="emerald"
              />
              <StatsCard
                icon={<ClipboardList className="w-8 h-8" />}
                value={stats.pending_tasks || 0}
                label="Pending Tasks"
                color="amber"
                badge={stats.tasks_needing_review > 0 ? `${stats.tasks_needing_review} to review` : null}
              />
              <StatsCard
                icon={<Users className="w-8 h-8" />}
                value={stats.assigned_workers || 0}
                label="Assigned Workers"
                color="purple"
              />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowEnhancedJobPosting(true)}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition"
                  data-testid="pm-ai-post-job-btn"
                >
                  <Sparkles className="w-5 h-5" />
                  Post Job with AI
                </button>
                <button
                  onClick={() => setActiveView('tasks')}
                  className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:shadow-lg transition"
                >
                  <ClipboardList className="w-5 h-5" />
                  Review Tasks ({stats.tasks_needing_review || 0})
                </button>
                <button
                  onClick={() => setActiveView('shifts')}
                  className="flex items-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition"
                >
                  <Eye className="w-5 h-5" />
                  View All Shifts
                </button>
              </div>
            </div>

            {/* Recent Shifts & Pending Reviews */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Shifts */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">Recent Shifts</h3>
                  <button
                    onClick={() => setActiveView('shifts')}
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                  >
                    View All <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {(dashboard?.recent_shifts || []).slice(0, 5).map(shift => (
                    <div key={shift._id} className="p-4 hover:bg-slate-50 transition">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800">{shift.title}</p>
                          <p className="text-sm text-slate-500">
                            {shift.location?.address || scope.location?.name || 'Location TBD'}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {shift.start_time ? new Date(shift.start_time).toLocaleString() : 'Date TBD'}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${shiftStatusClass(shift.status)}`}>
                          {shift.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {(!dashboard?.recent_shifts || dashboard.recent_shifts.length === 0) && (
                    <div className="p-8 text-center text-slate-500">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No shifts yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pending Reviews */}
              <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-amber-200 bg-amber-50">
                  <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Tasks Needing Review ({dashboard?.pending_reviews?.length || 0})
                  </h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {(dashboard?.pending_reviews || []).slice(0, 5).map(task => (
                    <div key={task._id} className="p-4 hover:bg-slate-50 transition">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800">{task.title}</p>
                          <p className="text-sm text-slate-500">
                            Priority: <span className={`font-medium ${
                              task.priority === 'high' || task.priority === 'urgent' ? 'text-red-600' : 'text-slate-600'
                            }`}>{task.priority}</span>
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => reviewTask(task._id, 'approved')}
                            className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                            title="Approve"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => reviewTask(task._id, 'rejected', 'Please revise and resubmit')}
                            className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                            title="Reject"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!dashboard?.pending_reviews || dashboard.pending_reviews.length === 0) && (
                    <div className="p-8 text-center">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                      <p className="text-slate-600 font-medium">All caught up!</p>
                      <p className="text-sm text-slate-500">No tasks pending review</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Shifts Tab */}
        {activeView === 'shifts' && (
          <ShiftsView 
            shifts={shifts}
            onSelectShift={(shift) => {
              setSelectedShift(shift);
              loadShiftTasks(shift._id);
            }}
            selectedShift={selectedShift}
            tasks={tasks}
            onAddTask={() => setShowTaskModal(true)}
            onReviewTask={reviewTask}
            onPostShift={() => setShowPostShiftModal(true)}
            onAIPostJob={() => setShowEnhancedJobPosting(true)}
            onRefresh={loadShifts}
          />
        )}

        {/* Tasks Tab */}
        {activeView === 'tasks' && (
          <TasksView 
            pendingReviews={dashboard?.pending_reviews || []}
            onReviewTask={reviewTask}
            onRefresh={loadDashboard}
          />
        )}

        {/* Analytics Tab */}
        {activeView === 'analytics' && (
          <AnalyticsView 
            programId={scope.program?._id}
            locationId={scope.location?._id}
          />
        )}
      </main>

      {/* Task Creation Modal */}
      {showTaskModal && selectedShift && (
        <CreateTaskModal
          jobId={selectedShift._id}
          templates={taskTemplates}
          onClose={() => setShowTaskModal(false)}
          onCreated={() => {
            setShowTaskModal(false);
            loadShiftTasks(selectedShift._id);
            toast.success('Task created successfully');
          }}
        />
      )}

      {/* Post Shift Modal */}
      {showPostShiftModal && (
        <PostShiftModal
          programId={scope.program?._id}
          locations={scope.locations || [scope.location].filter(Boolean)}
          onClose={() => setShowPostShiftModal(false)}
          onCreated={() => {
            setShowPostShiftModal(false);
            loadShifts();
            loadDashboard();
            toast.success('Shift posted successfully');
          }}
        />
      )}

      {/* Enhanced AI Job Posting Modal */}
      {showEnhancedJobPosting && (
        <EnhancedJobPosting
          onClose={() => setShowEnhancedJobPosting(false)}
          onJobCreated={() => {
            setShowEnhancedJobPosting(false);
            loadShifts();
            loadDashboard();
            toast.success('Job posted successfully with AI assistance');
          }}
        />
      )}
    </div>
  );
}

// Stats Card Component
function StatsCard({ icon, value, label, color, badge }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-xl ${colorClasses[color]}`}>
          {icon}
        </div>
        {badge && (
          <span className={`text-xs px-2 py-1 rounded-full ${
            badge.includes('review') ? 'bg-amber-100 text-amber-700 animate-pulse' : 'bg-slate-100 text-slate-600'
          }`}>
            {badge}
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-slate-800">{value}</p>
      <p className="text-slate-500">{label}</p>
    </div>
  );
}

// Shifts View Component
function ShiftsView({ shifts, onSelectShift, selectedShift, tasks, onAddTask, onReviewTask, onPostShift, onAIPostJob, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Shift Management</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={onAIPostJob}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition"
            data-testid="shifts-ai-post-btn"
          >
            <Sparkles className="w-5 h-5" />
            Post with AI
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Shifts List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">All Shifts ({shifts.length})</h3>
            <button onClick={onRefresh} className="p-2 hover:bg-slate-100 rounded-lg transition">
              <RefreshCw className="w-4 h-4 text-slate-600" />
            </button>
          </div>
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {shifts.map(shift => (
              <button
                key={shift._id}
                onClick={() => onSelectShift(shift)}
                className={`w-full p-4 text-left hover:bg-slate-50 transition ${
                  selectedShift?._id === shift._id ? 'bg-purple-50 border-l-4 border-purple-600' : ''
                }`}
              >
                <p className="font-medium text-slate-800">{shift.title}</p>
                <p className="text-sm text-slate-500 mt-1">
                  {shift.start_time ? new Date(shift.start_time).toLocaleString() : 'Date TBD'}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${shiftStatusClass(shift.status)}`}>
                    {shift.status}
                  </span>
                  {shift.worker_id && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Assigned
                    </span>
                  )}
                </div>
              </button>
            ))}
            {shifts.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No shifts found</p>
                <button
                  onClick={onPostShift}
                  className="mt-3 text-purple-600 hover:text-purple-700 font-medium"
                >
                  Post your first shift
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Shift Details & Tasks */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {selectedShift ? (
            <>
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                <h3 className="font-semibold text-slate-800">{selectedShift.title}</h3>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" />
                  {selectedShift.location?.address || 'Location TBD'}
                </p>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  <Clock className="w-4 h-4" />
                  {selectedShift.start_time ? new Date(selectedShift.start_time).toLocaleString() : 'Date TBD'}
                </p>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-slate-700">Tasks ({tasks.length})</h4>
                  <button
                    onClick={onAddTask}
                    className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 font-medium"
                  >
                    <Plus className="w-4 h-4" /> Add Task
                  </button>
                </div>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {tasks.map(task => (
                    <div key={task._id} className={`p-3 rounded-lg border ${taskRowClass(task.status)}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-800">{task.title}</p>
                          <p className="text-xs text-slate-500 capitalize mt-1">
                            Priority: <span className={task.priority === 'high' || task.priority === 'urgent' ? 'text-red-600 font-medium' : ''}>{task.priority}</span>
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${taskStatusClass(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                      {task.status === 'needs_review' && (
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => onReviewTask(task._id, 'approved')}
                            className="flex-1 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-1"
                          >
                            <Check className="w-4 h-4" /> Approve
                          </button>
                          <button
                            onClick={() => onReviewTask(task._id, 'rejected', 'Please revise')}
                            className="flex-1 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-1"
                          >
                            <X className="w-4 h-4" /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <div className="text-center py-6">
                      <ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-slate-500 text-sm">No tasks yet</p>
                      <button
                        onClick={onAddTask}
                        className="mt-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                      >
                        Add first task
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[400px] text-slate-500">
              <div className="text-center">
                <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="font-medium">Select a shift</p>
                <p className="text-sm">to view details and manage tasks</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Tasks View Component
function TasksView({ pendingReviews, onReviewTask, onRefresh }) {
  const [selectedTask, setSelectedTask] = useState(null);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50">
        <div>
          <h3 className="font-semibold text-slate-800">Tasks Needing Review ({pendingReviews.length})</h3>
          <p className="text-sm text-slate-500">Tasks completed by workers awaiting your approval</p>
        </div>
        <button onClick={onRefresh} className="p-2 hover:bg-white/50 rounded-lg transition">
          <RefreshCw className="w-4 h-4 text-slate-600" />
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {pendingReviews.map(task => (
          <div key={task._id} className="p-6 hover:bg-slate-50 transition">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{task.title}</h4>
                    <p className="text-sm text-slate-500">{task.job_title || 'Shift Task'}</p>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 mt-3 ml-13">{task.description || 'No description provided'}</p>
                
                {/* Worker Info */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-800">
                        Completed by: {task.worker_name || task.assigned_worker_name || 'Worker'}
                      </p>
                      <p className="text-xs text-blue-600">
                        Submitted: {task.submitted_at ? new Date(task.submitted_at).toLocaleString() : 
                                   task.updated_at ? new Date(task.updated_at).toLocaleString() : 'Recently'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Task Checklist if available */}
                {task.checklist && task.checklist.length > 0 && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-600 mb-2">Checklist Items:</p>
                    <div className="space-y-1">
                      {task.checklist.map((item, idx) => (
                        <div key={`${item.text || item}-${idx}`} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-gray-700">{item.text || item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Worker Notes */}
                {task.notes && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs font-medium text-amber-700 mb-1">Worker Notes:</p>
                    <p className="text-sm text-amber-800">{task.notes}</p>
                  </div>
                )}

                {/* Uploaded Photos */}
                {task.photos && task.photos.length > 0 && (
                  <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                    <p className="text-xs font-medium text-indigo-700 mb-2 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Uploaded Photos ({task.photos.length})
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {task.photos.map((photo, idx) => (
                        <div key={photo.url || photo || idx} className="relative group">
                          <img 
                            src={photo.url || photo} 
                            alt={`Task photo ${idx + 1}`}
                            className="w-full h-20 object-cover rounded-lg border border-indigo-200 cursor-pointer hover:opacity-90 transition"
                            onClick={() => window.open(photo.url || photo, '_blank')}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition rounded-lg flex items-center justify-center">
                            <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition" />
                          </div>
                          {photo.caption && (
                            <p className="text-xs text-indigo-600 mt-1 truncate">{photo.caption}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 mt-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${taskPriorityClass(task.priority)}`}>
                    {task.priority || 'normal'} priority
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                    Awaiting Review
                  </span>
                  {task.location_name && (
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" />
                      {task.location_name}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 ml-6">
                <button
                  onClick={() => onReviewTask(task._id, 'approved')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
                >
                  <Check className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={() => onReviewTask(task._id, 'rejected', 'Please revise and resubmit')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                >
                  <X className="w-4 h-4" /> Reject
                </button>
                <button
                  onClick={() => setSelectedTask(task)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" /> Details
                </button>
              </div>
            </div>
          </div>
        ))}
        {pendingReviews.length === 0 && (
          <div className="p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">All caught up!</p>
            <p className="text-sm text-slate-500 mt-1">No tasks pending review</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Analytics View Component
function AnalyticsView({ programId, locationId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [days]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await apiCall(`/api/employer/group-homes/analytics?days=${days}`);
      setAnalytics(response);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const kpis = analytics?.kpis || {};
  const summary = analytics?.summary || {};
  const topWorkers = analytics?.top_workers || [];
  const shiftsByLocation = analytics?.shifts_by_location || [];
  const fillRateTrend = analytics?.fill_rate_trend || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Program Analytics</h2>
          <p className="text-sm text-slate-500">Data populated from worker activities and shift completions</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadAnalytics} className="p-2 hover:bg-slate-100 rounded-lg transition">
            <RefreshCw className="w-4 h-4 text-slate-600" />
          </button>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-slate-700"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
          <Target className="w-8 h-8 opacity-80 mb-3" />
          <p className="text-3xl font-bold">{kpis.fill_rate || 0}%</p>
          <p className="text-white/80">Fill Rate</p>
          <p className="text-xs text-white/60 mt-1">{kpis.filled_shifts || 0} of {kpis.total_shifts || 0} shifts filled</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-5 text-white">
          <Clock className="w-8 h-8 opacity-80 mb-3" />
          <p className="text-3xl font-bold">{kpis.avg_time_to_fill_hours || 0}h</p>
          <p className="text-white/80">Avg Time to Fill</p>
          <p className="text-xs text-white/60 mt-1">From posting to assignment</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-5 text-white">
          <Briefcase className="w-8 h-8 opacity-80 mb-3" />
          <p className="text-3xl font-bold">{kpis.total_shifts || 0}</p>
          <p className="text-white/80">Total Shifts</p>
          <p className="text-xs text-white/60 mt-1">{kpis.active_shifts || 0} currently active</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
          <CheckCircle className="w-8 h-8 opacity-80 mb-3" />
          <p className="text-3xl font-bold">{kpis.completed_shifts || 0}</p>
          <p className="text-white/80">Completed</p>
          <p className="text-xs text-white/60 mt-1">${kpis.total_labor_cost?.toFixed(2) || '0.00'} total labor</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.programs || 0}</p>
              <p className="text-sm text-slate-500">Programs</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.locations || 0}</p>
              <p className="text-sm text-slate-500">Locations</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.workers_utilized || 0}</p>
              <p className="text-sm text-slate-500">Workers Utilized</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Workers */}
      {topWorkers.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <h3 className="font-semibold text-slate-800">Top Performing Workers</h3>
            <p className="text-sm text-slate-500">Based on completed shifts and reliability</p>
          </div>
          <div className="divide-y divide-slate-100">
            {topWorkers.map((worker, idx) => (
              <div key={worker.worker_id} className="p-4 hover:bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-slate-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{worker.name}</p>
                    <p className="text-sm text-slate-500">{worker.shifts_completed} shifts • {worker.locations_worked} locations</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">${worker.earnings?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-slate-500">Total Earned</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shifts by Location */}
      {shiftsByLocation.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-800">Shifts by Location</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {shiftsByLocation.map((loc) => (
              <div key={loc.location} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-slate-800">{loc.location}</p>
                  <span className="text-sm text-slate-500">{loc.filled}/{loc.total} filled ({loc.fill_rate?.toFixed(0)}%)</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${loc.fill_rate || 0}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {kpis.total_shifts === 0 && (
        <div className="bg-slate-50 rounded-xl p-8 text-center">
          <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-semibold text-slate-700 mb-2">No Analytics Data Yet</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Analytics will populate automatically as workers complete shifts and submit task reports. 
            Start by posting shifts and assigning workers.
          </p>
        </div>
      )}
    </div>
  );
}

// Create Task Modal
function CreateTaskModal({ jobId, templates, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [useTemplate, setUseTemplate] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    instructions: '',
    checklist: []
  });
  const [newItem, setNewItem] = useState('');

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setForm({
      ...form,
      title: template.name,
      description: template.description,
      priority: template.priority,
      checklist: template.checklist || []
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    setLoading(true);
    try {
      await apiCall(`/api/employer/shifts/${jobId}/tasks`, {
        method: 'POST',
        body: JSON.stringify({
          job_id: jobId,
          title: form.title,
          description: form.description,
          priority: form.priority,
          instructions: form.instructions,
          checklist: form.checklist,
          template_id: selectedTemplate?.id
        })
      });
      onCreated();
    } catch (error) {
      toast.error(error.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Create Task</h3>
            <p className="text-white/80 text-sm">Assign a task to this shift</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Template Toggle */}
          <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
            <button
              type="button"
              onClick={() => setUseTemplate(true)}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                useTemplate ? 'bg-white shadow text-purple-700' : 'text-slate-600'
              }`}
            >
              Use Template
            </button>
            <button
              type="button"
              onClick={() => {
                setUseTemplate(false);
                setSelectedTemplate(null);
                setForm({ title: '', description: '', priority: 'medium', instructions: '', checklist: [] });
              }}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                !useTemplate ? 'bg-white shadow text-purple-700' : 'text-slate-600'
              }`}
            >
              Custom Task
            </button>
          </div>

          {/* Template Selection */}
          {useTemplate && (
            <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
              {templates.builtin.map(template => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => handleTemplateSelect(template)}
                  className={`p-3 text-left rounded-xl border transition ${
                    selectedTemplate?.id === template.id 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <p className="font-medium text-slate-800 text-sm">{template.name}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</p>
                </button>
              ))}
            </div>
          )}

          {/* Form Fields */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Task Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Instructions</label>
            <textarea
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
              rows={2}
              placeholder="Special instructions for the worker..."
            />
          </div>

          {/* Checklist */}
          {form.checklist.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Checklist</label>
              <div className="space-y-1">
                {form.checklist.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm">
                    <CheckCircle className="w-4 h-4 text-slate-400" />
                    <span className="flex-1 text-slate-700">{item}</span>
                    <button
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        checklist: form.checklist.filter((_, i) => i !== idx)
                      })}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.title.trim()}
              className="px-6 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Post Shift Modal
function PostShiftModal({ programId, locations, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [taskInput, setTaskInput] = useState('');
  const [signatureMode, setSignatureMode] = useState('type');
  const [typedSignature, setTypedSignature] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    location_id: locations[0]?._id || '',
    pay_rate: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    required_workers: 1,
    required_skills: [],
    shift_tasks: [],
    allow_photo_uploads: false,
    task_verification: {
      manager_name: '',
      manager_signature: '',
      requirement_accepted: null,
      verified_at: null
    }
  });

  // Canvas drawing functions
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const { x, y } = getCanvasCoordinates(e, canvas);
    setIsDrawing(true);
    setLastPos({ x, y });
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setLastPos({ x, y });
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      const signatureData = canvasRef.current.toDataURL();
      setForm({
        ...form,
        task_verification: { ...form.task_verification, manager_signature: signatureData, requirement_accepted: null }
      });
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setForm({
      ...form,
      task_verification: { ...form.task_verification, manager_signature: '', requirement_accepted: null }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Combine date and time for submission
    const startDateTime = `${form.start_date}T${form.start_time}`;
    const endDateTime = `${form.end_date}T${form.end_time}`;
    const rangeErr = validateTimeRange(startDateTime, endDateTime, 'Shift end time');
    if (rangeErr) {
      toast.error(rangeErr);
      return;
    }
    setLoading(true);

    try {
      
      await apiCall('/api/employer/group-homes/shifts', {
        method: 'POST',
        body: JSON.stringify({
          program_id: programId,
          location_id: form.location_id,
          title: form.title,
          description: form.description,
          pay_rate: parseFloat(form.pay_rate),
          start_time: startDateTime,
          end_time: endDateTime,
          required_workers: form.required_workers,
          required_skills: form.required_skills,
          shift_tasks: form.shift_tasks,
          allow_photo_uploads: form.allow_photo_uploads,
          task_verification: form.task_verification
        })
      });
      onCreated();
    } catch (error) {
      toast.error(error.message || 'Failed to post shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Post New Shift</h3>
            <p className="text-white/80 text-sm">Create a shift for your program</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Shift Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
              placeholder="e.g., Day Shift Caregiver"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Location *</label>
            <select
              value={form.location_id}
              onChange={(e) => setForm({ ...form, location_id: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
              required
            >
              {locations.map(loc => (
                <option key={loc._id} value={loc._id}>{loc.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Time *</label>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                min={form.start_date || new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Time *</label>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Pay Rate ($/hr) *</label>
              <PayRateGate>
              <input
                type="number"
                step="0.01"
                value={form.pay_rate}
                onChange={(e) => setForm({ ...form, pay_rate: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                placeholder="25.00"
                required
              />
              </PayRateGate>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Workers Needed</label>
              <input
                type="number"
                min="1"
                value={form.required_workers}
                onChange={(e) => setForm({ ...form, required_workers: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-xl text-slate-800"
              rows={3}
              placeholder="Describe the shift responsibilities..."
            />
          </div>

          {/* Shift Tasks Section */}
          <div className="border-t border-slate-200 pt-4">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Shift Tasks</label>
            <p className="text-xs text-slate-500 mb-3">Add tasks workers must complete during this shift.</p>
            
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={taskInput}
                onChange={(e) => setTaskInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && taskInput.trim()) {
                    e.preventDefault();
                    setForm({
                      ...form,
                      shift_tasks: [...(form.shift_tasks || []), { id: Date.now(), text: taskInput.trim() }]
                    });
                    setTaskInput('');
                  }
                }}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm"
                placeholder="Enter a task..."
                style={{ color: '#111827' }}
              />
              <button
                type="button"
                onClick={() => {
                  if (taskInput.trim()) {
                    setForm({
                      ...form,
                      shift_tasks: [...(form.shift_tasks || []), { id: Date.now(), text: taskInput.trim() }]
                    });
                    setTaskInput('');
                  }
                }}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>

            {(form.shift_tasks || []).length > 0 && (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 mb-3">
                {form.shift_tasks.map((task, idx) => (
                  <div key={task.id || idx} className="flex items-center gap-2 p-2">
                    <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-medium">{idx + 1}</span>
                    <span className="flex-1 text-sm text-slate-700">{task.text}</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, shift_tasks: form.shift_tasks.filter((_, i) => i !== idx) })}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Allow Photo Uploads */}
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200 mb-3">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="allow_photos"
                  checked={form.allow_photo_uploads || false}
                  onChange={(e) => setForm({ ...form, allow_photo_uploads: e.target.checked })}
                  className="w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600"
                />
                <label htmlFor="allow_photos" className="text-sm text-indigo-800 cursor-pointer">
                  <span className="font-medium">Allow Workers to Upload Photos</span>
                  <p className="text-xs text-indigo-600 mt-0.5">Workers can attach images as proof of task completion</p>
                </label>
              </div>
            </div>

            {/* Task Verification */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Task Verification Required</p>
                  <p className="text-xs text-amber-700">Staff must verify task completion with name and signature.</p>
                </div>
              </div>

              {form.task_verification?.requirement_accepted !== null ? (
                <div className={`p-2 rounded-lg ${form.task_verification.requirement_accepted ? 'bg-green-100 border border-green-200' : 'bg-red-100 border border-red-200'}`}>
                  <p className={`text-xs font-medium ${form.task_verification.requirement_accepted ? 'text-green-700' : 'text-red-700'}`}>
                    {form.task_verification.requirement_accepted ? '✓ Requirement Accepted' : '✗ Requirement Denied'}
                  </p>
                  <p className="text-xs text-slate-600">Signed by: {form.task_verification.manager_name}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 mb-3">
                    <input
                      type="text"
                      value={form.task_verification?.manager_name || ''}
                      onChange={(e) => setForm({
                        ...form,
                        task_verification: { ...form.task_verification, manager_name: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm text-slate-800"
                      placeholder="Manager Name *"
                      style={{ color: '#111827' }}
                    />
                    
                    <div className="flex bg-amber-100 rounded-lg p-1">
                      <button type="button" onClick={() => setSignatureMode('type')}
                        className={`flex-1 px-2 py-1 text-xs rounded ${signatureMode === 'type' ? 'bg-white shadow-sm' : ''}`}>
                        Type
                      </button>
                      <button type="button" onClick={() => setSignatureMode('draw')}
                        className={`flex-1 px-2 py-1 text-xs rounded ${signatureMode === 'draw' ? 'bg-white shadow-sm' : ''}`}>
                        Draw
                      </button>
                    </div>

                    {signatureMode === 'type' ? (
                      <input
                        type="text"
                        value={typedSignature}
                        onChange={(e) => {
                          setTypedSignature(e.target.value);
                          setForm({
                            ...form,
                            task_verification: { ...form.task_verification, manager_signature: e.target.value }
                          });
                        }}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg text-lg text-blue-800"
                        placeholder="Type signature"
                        style={{ fontFamily: 'cursive', color: '#1e40af' }}
                      />
                    ) : (
                      <div>
                        <canvas
                          ref={canvasRef}
                          width={300}
                          height={60}
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                          className="w-full border border-amber-300 rounded-lg bg-white cursor-crosshair"
                          style={{ touchAction: 'none', maxHeight: '60px' }}
                        />
                        <button type="button" onClick={clearCanvas} className="text-xs text-amber-600 mt-1">Clear</button>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        let sig = form.task_verification?.manager_signature || '';
                        if (signatureMode === 'draw' && canvasRef.current) sig = canvasRef.current.toDataURL();
                        if (!form.task_verification?.manager_name || !sig) {
                          toast.error('Please enter name and signature');
                          return;
                        }
                        setForm({
                          ...form,
                          task_verification: { ...form.task_verification, manager_signature: sig, requirement_accepted: true, verified_at: new Date().toISOString() }
                        });
                        toast.success('Requirement accepted');
                      }}
                      className="flex-1 px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600"
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        let sig = form.task_verification?.manager_signature || '';
                        if (signatureMode === 'draw' && canvasRef.current) sig = canvasRef.current.toDataURL();
                        if (!form.task_verification?.manager_name || !sig) {
                          toast.error('Please enter name and signature');
                          return;
                        }
                        setForm({
                          ...form,
                          task_verification: { ...form.task_verification, manager_signature: sig, requirement_accepted: false, verified_at: new Date().toISOString() }
                        });
                        toast.info('Requirement denied');
                      }}
                      className="flex-1 px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600"
                    >
                      Deny
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Post Shift
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
